/**
 * Tenant context Planner needs to boot quickly in embed mode.
 *
 * Per the planner's embed-mode contract, these are passed as hints for
 * telemetry / feature-gate targeting and to skip a tenant lookup — they are
 * never used for authorization (all planner data calls remain auth-scoped).
 */
export type AiPlannerEmbedContext = {
	cloudId: string;
	/** Null for anonymous users / before tenant context initializes. */
	accountId: string | null;
};

type PlannerSearchParamsInput = string | URLSearchParams;

const HOST_OWNED_PLANNER_PARAMS = [
	'embed',
	'cloudId',
	'accountId',
	'bifrost-override',
	'ai-planner-bifrost-override',
];

/**
 * Builds the Planner embed URL that the iframe modal renders.
 *
 * The path mirrors the AI planning link convention:
 * - a specific plan resolves to `<base>/plan/:planId`
 * - the planner overview / create flow resolves to `<base>`
 *
 * Embed mode is requested via query params (the planner's documented contract):
 * - `embed=true` — renders the planner without its top nav / global sidebar
 * - `cloudId` — lets the planner skip the `/_edge/tenant_info` round-trip
 * - `accountId` — seeds the planner's telemetry / feature-gate targeting
 *
 * Keeping this in one place means the iframe modal and any consumer wiring
 * stay in sync with the planner embed contract.
 */
// The planner is served from the same origin as Jira, so the iframe uses the
// relative `/planner` base. A specific planner build can still be pinned at
// runtime via the `ai-planner-bifrost-override` query param (see below).
const PLANNER_BASE_PATH = '/planner';

/**
 * The planner's origin, when it is served from a different origin than Jira.
 * Returns `null` when the planner is same-origin (the relative `/planner` base),
 * in which case preconnecting is unnecessary. Used to warm the connection
 * (preconnect) before the iframe mounts.
 */
export const getAiPlannerOrigin = (): string | null => {
	try {
		// An absolute base has an origin distinct from a relative path.
		const url = new URL(PLANNER_BASE_PATH, 'https://relative.invalid');
		return url.origin === 'https://relative.invalid' ? null : url.origin;
	} catch {
		return null;
	}
};

/**
 * Reads the `ai-planner-bifrost-override` query param from the current Jira page
 * URL and forwards it to the planner iframe so a specific planner Bifrost build
 * can be pinned via the URL (e.g. for QA, dogfooding, or pre-release validation).
 *
 * Returns `null` when the param is absent or when running outside the browser
 * (SSR), in which case no override is appended and the planner serves its
 * default build.
 */
export const getBifrostOverrideFromLocation = (): string | null => {
	if (typeof globalThis.location === 'undefined') {
		return null;
	}
	const value = new URLSearchParams(globalThis.location.search).get('ai-planner-bifrost-override');
	return value != null && value !== '' ? value : null;
};

export const getAiPlannerUrl = (
	embedContext: AiPlannerEmbedContext,
	planId?: string,
	// When set, forwarded to the planner as `bifrost-override` so a specific
	// planner Bifrost build can be pinned (sourced from the Jira page URL's
	// `ai-planner-bifrost-override` query param via getBifrostOverrideFromLocation).
	bifrostOverride?: string,
	plannerSearch?: PlannerSearchParamsInput,
): string => {
	const path =
		planId != null && planId !== ''
			? `${PLANNER_BASE_PATH}/plan/${encodeURIComponent(planId)}`
			: PLANNER_BASE_PATH;

	const params = new URLSearchParams(plannerSearch);
	for (const param of HOST_OWNED_PLANNER_PARAMS) {
		params.delete(param);
	}

	params.set('embed', 'true');
	params.set('cloudId', embedContext.cloudId);
	if (embedContext.accountId != null && embedContext.accountId !== '') {
		params.set('accountId', embedContext.accountId);
	}
	if (bifrostOverride != null && bifrostOverride !== '') {
		params.set('bifrost-override', bifrostOverride);
	}

	return `${path}?${params.toString()}`;
};
